import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import Layout from '../components/Layout';
import { ordersAPI } from '../services/api';
import { useToast } from '../components/Toast';
import { Search, Eye, RefreshCw, X, Truck, CheckCircle, Bell, Package, Download, ChevronLeft, ChevronRight } from 'lucide-react';

// Admin can only dispatch / deliver. Preparing & Ready are set by the seller.
// The backend pushes a notification to the customer for these two.
const NOTIFIES_CUSTOMER = new Set(['OUT_FOR_DELIVERY', 'DELIVERED']);

function maskOrderId(id) {
  if (!id) return '—';
  const s = String(id).replace(/^#/, '');
  if (s.length <= 6) return '#' + s;
  return '#' + s.slice(0, 3) + '*'.repeat(s.length - 6) + s.slice(-3);
}

const STATUS_OPTS = [
  { value: 'ALL',              label: 'All Active' },
  { value: 'PREPARING',        label: 'Preparing' },
  { value: 'READY',            label: 'Ready' },
  { value: 'OUT_FOR_DELIVERY', label: 'Out for Delivery' },
  { value: 'DELIVERED',        label: 'Delivered' },
  { value: 'CANCELLED',        label: 'Cancelled' },
];

// ── Ready Notification Popup ──────────────────────────────────────────
function ReadyNotificationPopup({ orders, onDismiss, onDispatch }) {
  if (!orders || orders.length === 0) return null;
  return (
    <div className="modal-overlay" style={{ zIndex: 9999 }}>
      <div className="modal" style={{ maxWidth: 480, borderTop: '4px solid #f97316' }}>
        <div className="modal-header" style={{ background: 'linear-gradient(135deg, #fff7ed, #fff)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#f97316', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Package size={20} color="#fff" />
            </div>
            <div>
              <div className="modal-title" style={{ color: '#c2410c' }}>
                {orders.length === 1 ? '🔔 Order Ready for Dispatch!' : `🔔 ${orders.length} Orders Ready for Dispatch!`}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>Seller ne order ready kar diya — abhi dispatch karo</div>
            </div>
          </div>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onDismiss}><X size={16} /></button>
        </div>
        <div style={{ padding: '16px 20px', maxHeight: 280, overflowY: 'auto' }}>
          {orders.map(order => (
            <div key={order.orderId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', marginBottom: 8, borderRadius: 10, background: '#fff7ed', border: '1px solid #fed7aa' }}>
              <div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 13, color: '#c2410c', fontWeight: 700 }}>{maskOrderId(order.displayId || order.orderId)}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{order.customerName} · ₹{Number(order.amount).toLocaleString('en-IN')}</div>
                {order.sellerName && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Seller: {order.sellerName}</div>}
              </div>
              <button className="btn btn-sm" style={{ background: '#f97316', color: '#fff', fontSize: 11, padding: '5px 12px', borderRadius: 8, display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }} onClick={() => onDispatch(order.orderId)}>
                <Truck size={12} /> Dispatch
              </button>
            </div>
          ))}
        </div>
        <div className="modal-footer" style={{ gap: 8 }}>
          <button className="btn btn-ghost" onClick={onDismiss}>Dismiss</button>
          <button className="btn" style={{ background: '#f97316', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={() => { orders.forEach(o => onDispatch(o.orderId)); onDismiss(); }}>
            <Truck size={14} /> Dispatch All
          </button>
        </div>
      </div>
    </div>
  );
}

// ── New Order Notification Popup ──────────────────────────────────────
function NewOrderNotificationPopup({ orders, onDismiss, onView }) {
  if (!orders || orders.length === 0) return null;
  return (
    <div className="modal-overlay" style={{ zIndex: 9999 }}>
      <div className="modal" style={{ maxWidth: 480, borderTop: '4px solid var(--green)' }}>
        <div className="modal-header" style={{ background: 'linear-gradient(135deg, #f0fdf4, #fff)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--green)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Bell size={20} color="#fff" />
            </div>
            <div>
              <div className="modal-title" style={{ color: 'var(--primary-dark)' }}>
                {orders.length === 1 ? '🔔 New Order Placed!' : `🔔 ${orders.length} New Orders Placed!`}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>A customer just placed an order</div>
            </div>
          </div>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onDismiss}><X size={16} /></button>
        </div>
        <div style={{ padding: '16px 20px', maxHeight: 280, overflowY: 'auto' }}>
          {orders.map(order => (
            <div key={order.orderId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', marginBottom: 8, borderRadius: 10, background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
              <div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 13, color: 'var(--primary-dark)', fontWeight: 700 }}>{maskOrderId(order.displayId || order.orderId)}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{order.customerName} · ₹{Number(order.amount).toLocaleString('en-IN')}</div>
                {order.sellerName && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Seller: {order.sellerName}</div>}
              </div>
              <button className="btn btn-sm" style={{ background: 'var(--green)', color: '#fff', fontSize: 11, padding: '5px 12px', borderRadius: 8, display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }} onClick={() => onView(order.orderId)}>
                <Eye size={12} /> View
              </button>
            </div>
          ))}
        </div>
        <div className="modal-footer" style={{ gap: 8 }}>
          <button className="btn btn-ghost" onClick={onDismiss}>Dismiss</button>
        </div>
      </div>
    </div>
  );
}

// ── Order Detail Modal ────────────────────────────────────────────────
function OrderDetailModal({ orderId, onClose }) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    ordersAPI.getById(orderId).then(r => { setOrder(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [orderId]);
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 580 }}>
        <div className="modal-header">
          <span className="modal-title">Order Details</span>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        {loading ? <div className="page-loader"><div className="spinner" /></div>
          : !order ? <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 32 }}>Could not load order</p>
          : (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 14, color: 'var(--primary-dark)' }}>{maskOrderId(order.displayId || order.orderId)}</span>
                <span className={`badge ${order.status?.toLowerCase().replaceAll('_', '-')}`}>{order.status?.replaceAll('_', ' ')}</span>
              </div>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Order Items</div>
                {(order.items || []).map((item, i) => (
                  <div key={item.productId || item.productName || i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                    <span style={{ color: 'var(--text-primary)' }}>{item.productName} <span style={{ color: 'var(--text-muted)' }}>× {item.quantity}</span></span>
                    <span style={{ color: 'var(--green)', fontWeight: 600 }}>₹{Number((item.price ?? item.totalPrice ?? item.unitPrice ?? 0) * (item.quantity ?? 1)).toLocaleString('en-IN')}</span>
                  </div>
                ))}
              </div>
              {[['Customer', order.customerName], ['Seller', order.sellerName || order.seller?.businessName || '—'], ['Address', order.deliveryAddress], ['Payment', order.paymentMethod], ['Total Amount', `₹${Number(order.totalAmount).toLocaleString('en-IN')}`], ['Placed At', order.createdAt ? new Date(order.createdAt).toLocaleString('en-IN') : '—']].map(([k, v]) => (
                <div className="detail-row" key={k}><span className="detail-label">{k}</span><span className="detail-value" style={{ textAlign: 'right', maxWidth: '60%' }}>{v || '—'}</span></div>
              ))}
            </div>
          )}
        <div className="modal-footer"><button className="btn btn-ghost" onClick={onClose}>Close</button></div>
      </div>
    </div>
  );
}

// ── Mobile Order Card ─────────────────────────────────────────────────
function MobileOrderCard({ order, onView, onDispatch, onDeliver, updatingId }) {
  const statusColor = {
    DELIVERED: 'var(--green)', NEW: 'var(--yellow)',
    OUT_FOR_DELIVERY: 'var(--blue)', CANCELLED: 'var(--red)', READY: '#f97316',
    PREPARING: '#a855f7',
  }[order.status] || 'var(--border)';
  const isUpdating = updatingId === order.orderId;

  return (
    <div style={{
      background: order.status === 'READY' ? '#fff7ed' : '#fff',
      border: `1px solid ${order.status === 'READY' ? '#fed7aa' : 'var(--border)'}`,
      borderRadius: 16,
      marginBottom: 12,
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      position: 'relative',
      width: '100%',
      boxSizing: 'border-box',
    }}>
      {/* Left accent bar */}
      <div style={{
        position: 'absolute', left: 0, top: 0, bottom: 0, width: 4,
        background: statusColor,
        borderRadius: '16px 0 0 16px',
      }} />

      {/* Card body */}
      <div style={{ padding: '14px 12px 14px 20px', boxSizing: 'border-box' }}>

        {/* Row 1: Order ID + Status + View */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 8 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 13, color: 'var(--primary-dark)', fontWeight: 700, whiteSpace: 'nowrap' }}>
                {maskOrderId(order.displayId || order.orderId)}
              </span>
              {order.status === 'READY' && (
                <span style={{ fontSize: 9, background: '#f97316', color: '#fff', borderRadius: 4, padding: '2px 6px', fontWeight: 700, letterSpacing: 0.3, flexShrink: 0 }}>READY</span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            <span className={`badge ${order.status?.toLowerCase().replaceAll('_', '-')}`} style={{ fontSize: 10.5, whiteSpace: 'nowrap' }}>
              {order.status?.replaceAll('_', ' ') || '—'}
            </span>
            <button
              onClick={(e) => { e.stopPropagation(); onView(order.orderId); }}
              style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--bg-hover)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, WebkitTapHighlightColor: 'transparent' }}
            >
              <Eye size={13} color="var(--text-secondary)" />
            </button>
          </div>
        </div>

        {/* Row 2: Customer + Seller */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 3 }}>Customer</div>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.3 }}>{order.customerName || '—'}</div>
          </div>
          <div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 3 }}>Seller</div>
            <div style={{ fontSize: 13.5, color: 'var(--text-secondary)', lineHeight: 1.3 }}>{order.sellerName || '—'}</div>
          </div>
        </div>

        {/* Row 3: Items */}
        {order.productNames && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 3 }}>Items</div>
            <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.4 }}>
              {order.productNames}
            </div>
          </div>
        )}

        {/* Row 4: Amount + Date */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: order.status === 'DELIVERED' || order.status === 'CANCELLED' ? 0 : 12 }}>
          <span style={{ fontSize: 19, fontWeight: 800, color: 'var(--green)', letterSpacing: -0.8 }}>
            ₹{Number(order.amount).toLocaleString('en-IN')}
          </span>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {order.createdAt ? new Date(order.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' }) : '—'}
          </span>
        </div>

        {/* Action Buttons — only for actionable statuses */}
        {/* Preparing / Ready are handled by the seller — admin only sees them */}
        {order.status === 'NEW' && (
          <div style={{ background: 'var(--bg-hover)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px', fontSize: 12.5, fontWeight: 600, color: 'var(--text-muted)', textAlign: 'center' }}>
            ⏳ Waiting for seller to accept
          </div>
        )}

        {order.status === 'PREPARING' && (
          <div style={{ background: 'rgba(168,85,247,0.08)', border: '1px solid rgba(168,85,247,0.25)', borderRadius: 10, padding: '10px', fontSize: 12.5, fontWeight: 600, color: '#a855f7', textAlign: 'center' }}>
            👩‍🍳 Seller is preparing this order
          </div>
        )}

        {order.status === 'READY' && (
          <button
            onClick={() => onDispatch(order.orderId)}
            disabled={isUpdating}
            style={{ width: '100%', background: '#f97316', color: '#fff', border: 'none', borderRadius: 10, padding: '11px', fontSize: 13.5, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, cursor: isUpdating ? 'not-allowed' : 'pointer', opacity: isUpdating ? 0.7 : 1, WebkitTapHighlightColor: 'transparent' }}
          >
            {isUpdating ? <><div style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} /> Updating...</> : <><Truck size={15} /> Dispatch Now</>}
          </button>
        )}

        {order.status === 'OUT_FOR_DELIVERY' && (
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1, background: 'rgba(249,115,22,0.08)', border: '1px solid #fed7aa', borderRadius: 10, padding: '10px', fontSize: 13, fontWeight: 600, color: '#ea580c', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
              🚚 On the way
            </div>
            <button
              onClick={() => onDeliver(order.orderId)}
              disabled={isUpdating}
              style={{ flex: 1, background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 10, padding: '10px', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, cursor: isUpdating ? 'not-allowed' : 'pointer', opacity: isUpdating ? 0.7 : 1, WebkitTapHighlightColor: 'transparent' }}
            >
              {isUpdating ? '...' : <><CheckCircle size={14} /> Delivered</>}
            </button>
          </div>
        )}

        {order.status === 'DELIVERED' && (
          <div style={{ background: 'var(--green-bg)', border: '1px solid rgba(34,197,94,0.25)', borderRadius: 10, padding: '10px', fontSize: 13, fontWeight: 700, color: 'var(--green)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <CheckCircle size={14} /> Delivered
          </div>
        )}
      </div>
    </div>
  );
}

MobileOrderCard.propTypes = {
  order: PropTypes.shape({
    orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    displayId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    status: PropTypes.string,
    customerName: PropTypes.string,
    sellerName: PropTypes.string,
    productNames: PropTypes.string,
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    createdAt: PropTypes.string,
  }).isRequired,
  onView: PropTypes.func.isRequired,
  onDispatch: PropTypes.func.isRequired,
  onDeliver: PropTypes.func.isRequired,
  updatingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

MobileOrderCard.defaultProps = {
  updatingId: null,
};

// ── Main Orders Page ──────────────────────────────────────────────────
export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  // Mobile infinite scroll state
  const [mobileOrders, setMobileOrders] = useState([]);
  const [mobilePage, setMobilePage] = useState(0);
  const [mobileHasMore, setMobileHasMore] = useState(true);
  const [mobileLoadingMore, setMobileLoadingMore] = useState(false);
  const loaderRef = useRef(null);

  const toast = useToast();
  const [readyPopupOrders, setReadyPopupOrders] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const audioCtxRef = useRef(null);
  const knownReadyIds = useRef(new Set());
  const isFirstPoll = useRef(true);

  // ── New-order detection (mirrors the ready-order check below) ────────
  const [newOrderPopupOrders, setNewOrderPopupOrders] = useState([]);
  const [showNewOrderPopup, setShowNewOrderPopup] = useState(false);
  const knownNewIds = useRef(new Set());
  const isFirstNewPoll = useRef(true);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  useEffect(() => {
    const initCtx = () => {
      if (audioCtxRef.current) return;
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    };
    document.addEventListener('click', initCtx, { once: true });
    document.addEventListener('keydown', initCtx, { once: true });
    document.addEventListener('touchstart', initCtx, { once: true });
    return () => {
      document.removeEventListener('click', initCtx);
      document.removeEventListener('keydown', initCtx);
      document.removeEventListener('touchstart', initCtx);
    };
  }, []);

  const playNotification = useCallback(() => {
    const ctx = audioCtxRef.current;
    if (!ctx) return;
    const doPlay = () => {
      const beep = (freq, startDelay, duration) => {
        const osc = ctx.createOscillator(); const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, ctx.currentTime + startDelay);
        gain.gain.setValueAtTime(0, ctx.currentTime + startDelay);
        gain.gain.linearRampToValueAtTime(0.7, ctx.currentTime + startDelay + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + startDelay + duration);
        osc.start(ctx.currentTime + startDelay);
        osc.stop(ctx.currentTime + startDelay + duration);
      };
      beep(700, 0, 0.35); beep(1050, 0.25, 0.45);
    };
    if (ctx.state === 'suspended') ctx.resume().then(doPlay); else doPlay();
  }, []);

  // Distinct chime (three quick ascending notes) so a "new order placed"
  // sound is never confused with the "order ready for dispatch" sound above.
  const playNewOrderChime = useCallback(() => {
    const ctx = audioCtxRef.current;
    if (!ctx) return;
    const doPlay = () => {
      const beep = (freq, startDelay, duration) => {
        const osc = ctx.createOscillator(); const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, ctx.currentTime + startDelay);
        gain.gain.setValueAtTime(0, ctx.currentTime + startDelay);
        gain.gain.linearRampToValueAtTime(0.6, ctx.currentTime + startDelay + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + startDelay + duration);
        osc.start(ctx.currentTime + startDelay);
        osc.stop(ctx.currentTime + startDelay + duration);
      };
      beep(600, 0, 0.15); beep(750, 0.15, 0.15); beep(900, 0.3, 0.35);
    };
    if (ctx.state === 'suspended') ctx.resume().then(doPlay); else doPlay();
  }, []);

  const handleStatusUpdate = async (orderId, newStatus) => {
    setUpdatingId(orderId);
    try {
      await ordersAPI.updateStatus(orderId, newStatus);
      toast(
        `Order marked as ${newStatus.replaceAll('_', ' ')}${NOTIFIES_CUSTOMER.has(newStatus) ? ' · customer notified' : ''}`,
        'success'
      );
      // Update both desktop orders and mobile orders
      const updater = prev => prev.map(o => o.orderId === orderId ? { ...o, status: newStatus } : o);
      setOrders(updater);
      setMobileOrders(updater);
      if (newStatus === 'OUT_FOR_DELIVERY') {
        setReadyPopupOrders(prev => prev.filter(o => o.orderId !== orderId));
        knownReadyIds.current.delete(orderId);
      }
    } catch {
      toast('Failed to update order status', 'error');
    } finally {
      setUpdatingId(null);
    }
  };

  const exportAllCSV = useCallback(async () => {
    setExportLoading(true);
    toast('Exporting orders...', 'info');
    try {
      const statusParam = statusFilter === 'ALL' ? undefined : statusFilter;
      const firstRes = await ordersAPI.getAll(statusParam, 0, 1);
      const totalCount = firstRes.data?.totalElements || 0;
      if (totalCount === 0) { toast('No orders to export', 'error'); setExportLoading(false); return; }
      const allRes = await ordersAPI.getAll(statusParam, 0, totalCount);
      const allOrders = allRes.data?.content || [];
      const headers = ['Order ID', 'Customer', 'Seller', 'Products', 'Amount (INR)', 'Status', 'Date', 'Time'];
      const fmt = (d) => { if (!d) return ''; const dt = new Date(d); return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${dt.getFullYear()}`; };
      const fmtT = (d) => { if (!d) return ''; const dt = new Date(d); return `${String(dt.getHours()).padStart(2,'0')}:${String(dt.getMinutes()).padStart(2,'0')}`; };
      const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const rows = allOrders.map(o => [esc(o.displayId || o.orderId), esc(o.customerName || ''), esc(o.sellerName || ''), esc(o.productNames || ''), esc(o.amount ?? 0), esc(o.status || ''), esc(fmt(o.createdAt)), esc(fmtT(o.createdAt))]);
      const csv = '\uFEFF' + [headers.map(esc).join(','), ...rows.map(r => r.join(','))].join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `orders_${statusFilter === 'ALL' ? 'all' : statusFilter.toLowerCase()}_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast(`Exported ${allOrders.length} orders successfully`, 'success');
    } catch { toast('Export failed. Please try again.', 'error'); }
    finally { setExportLoading(false); }
  }, [statusFilter, toast]);

  // ── Order event check (manual — no more background polling) ──────────
  // This used to run on a 3s setInterval. It now runs once on page load
  // and again whenever the admin hits the refresh button, so "New Order"
  // and "Ready for Dispatch" popups are still available on demand without
  // any background network traffic.
  const checkForOrderEvents = useCallback(async () => {
    try {
      const res = await ordersAPI.getAll('NEW,READY', 0, 100);
      const combined = res.data?.content || [];
      const readyOrders = combined.filter(o => o.status === 'READY');
      const freshOrders = combined.filter(o => o.status === 'NEW');

      // -- Ready-for-dispatch detection --
      const currentReadyIds = new Set(readyOrders.map(o => o.orderId));
      if (!isFirstPoll.current) {
        const newReady = readyOrders.filter(o => !knownReadyIds.current.has(o.orderId));
        if (newReady.length > 0) {
          playNotification();
          setReadyPopupOrders(prev => { const ids = new Set(prev.map(o => o.orderId)); return [...prev, ...newReady.filter(o => !ids.has(o.orderId))]; });
          setShowPopup(true);
        }
      } else {
        isFirstPoll.current = false;
        if (readyOrders.length > 0) { setReadyPopupOrders(readyOrders); setShowPopup(true); }
      }
      knownReadyIds.current = currentReadyIds;

      // -- New-order detection --
      const currentNewIds = new Set(freshOrders.map(o => o.orderId));
      if (!isFirstNewPoll.current) {
        const justArrived = freshOrders.filter(o => !knownNewIds.current.has(o.orderId));
        if (justArrived.length > 0) {
          playNewOrderChime();
          setNewOrderPopupOrders(prev => { const ids = new Set(prev.map(o => o.orderId)); return [...prev, ...justArrived.filter(o => !ids.has(o.orderId))]; });
          setShowNewOrderPopup(true);
        }
      } else {
        isFirstNewPoll.current = false;
        // Don't pop up for orders that already existed when the page loaded —
        // only for ones placed/readied since the last check.
      }
      knownNewIds.current = currentNewIds;
    } catch {}
  }, [playNotification, playNewOrderChime]);

  // Desktop load (paginated)
  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const statusParam = statusFilter === 'ALL' ? undefined : statusFilter;
      const res = await ordersAPI.getAll(statusParam, page, 10);
      setOrders(res.data?.content || []);
      setTotalPages(res.data?.totalPages || 0);
      setTotal(res.data?.totalElements || 0);
    } catch { if (!silent) toast('Failed to load orders', 'error'); }
    finally { setLoading(false); }
  }, [page, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mobile: load first page fresh (on filter change)
  const loadMobileFresh = useCallback(async () => {
    setLoading(true);
    setMobileOrders([]);
    setMobilePage(0);
    setMobileHasMore(true);
    try {
      const statusParam = statusFilter === 'ALL' ? undefined : statusFilter;
      const res = await ordersAPI.getAll(statusParam, 0, 10);
      const content = res.data?.content || [];
      setMobileOrders(content);
      setTotal(res.data?.totalElements || 0);
      setMobilePage(1);
      setMobileHasMore(content.length === 10 && (res.data?.totalPages || 0) > 1);
    } catch { toast('Failed to load orders', 'error'); }
    finally { setLoading(false); }
  }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mobile: load next page (append)
  const loadMobileMore = useCallback(async () => {
    if (mobileLoadingMore || !mobileHasMore) return;
    setMobileLoadingMore(true);
    try {
      const statusParam = statusFilter === 'ALL' ? undefined : statusFilter;
      const res = await ordersAPI.getAll(statusParam, mobilePage, 10);
      const content = res.data?.content || [];
      setMobileOrders(prev => [...prev, ...content]);
      setMobilePage(p => p + 1);
      setMobileHasMore(content.length === 10 && mobilePage + 1 < (res.data?.totalPages || 0));
    } catch {}
    finally { setMobileLoadingMore(false); }
  }, [mobilePage, mobileHasMore, mobileLoadingMore, statusFilter]);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    if (!isMobile) return;
    const el = loaderRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) loadMobileMore();
    }, { threshold: 0.1 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [isMobile, loadMobileMore]);

  useEffect(() => {
    if (isMobile) loadMobileFresh();
    else load();
  }, [isMobile, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (!isMobile) load(); }, [page]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!isMobile) setPage(0); }, [statusFilter]);

  // One-time check on mount for orders that were already NEW/READY when the
  // page loaded. No more setInterval polling after this — use the refresh
  // button (handleRefresh below) to check again.
  useEffect(() => { checkForOrderEvents(); }, [checkForOrderEvents]);

  // Manual refresh: reloads the table AND re-checks for new/ready orders,
  // replacing the old 3s/10s background polling.
  const handleRefresh = useCallback(() => {
    if (isMobile) loadMobileFresh(); else load();
    checkForOrderEvents();
  }, [isMobile, loadMobileFresh, load, checkForOrderEvents]);

  // Client search
  const filtered = (() => {
    const source = isMobile ? mobileOrders : orders;
    if (!search) return source;
    const query = search.toLowerCase();
    const words = query.split(/\s+/).filter(Boolean);
    return source.map(o => {
      const orderId = (o.displayId || o.orderId || '').toLowerCase();
      const customer = (o.customerName || '').toLowerCase();
      const seller = (o.sellerName || '').toLowerCase();
      const products = (o.productNames || '').toLowerCase();
      let sc = 0;
      if (orderId.includes(query)) sc += 1000;
      if (customer === query) sc += 800; if (customer.startsWith(query)) sc += 500;
      if (seller === query) sc += 600; if (seller.startsWith(query)) sc += 400;
      words.forEach(w => { if (customer.includes(w)) sc += 50; if (seller.includes(w)) sc += 40; if (products.includes(w)) sc += 30; if (orderId.includes(w)) sc += 80; });
      return { o, sc };
    }).filter(x => x.sc > 0).sort((a, b) => b.sc - a.sc).map(x => x.o);
  })();

  return (
    <Layout title="Orders" subtitle="Monitor and manage all orders">

      {showNewOrderPopup && newOrderPopupOrders.length > 0 && (
        <NewOrderNotificationPopup
          orders={newOrderPopupOrders}
          onDismiss={() => setShowNewOrderPopup(false)}
          onView={(orderId) => { setSelectedOrderId(orderId); setShowNewOrderPopup(false); }}
        />
      )}

      {showPopup && readyPopupOrders.length > 0 && (
        <ReadyNotificationPopup
          orders={readyPopupOrders}
          onDismiss={() => setShowPopup(false)}
          onDispatch={(orderId) => { handleStatusUpdate(orderId, 'OUT_FOR_DELIVERY'); if (readyPopupOrders.length <= 1) setShowPopup(false); }}
        />
      )}

      {/* Status filter chips */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
        <style>{`div::-webkit-scrollbar{display:none}`}</style>
        {STATUS_OPTS.map(({ value, label }) => (
          <button key={value} className={`chip ${statusFilter === value ? 'active' : ''}`} onClick={() => setStatusFilter(value)} style={{ flexShrink: 0, whiteSpace: 'nowrap', fontSize: 11, padding: '4px 10px', minHeight: 28 }}>
            {label}
          </button>
        ))}
      </div>

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, width: '100%', boxSizing: 'border-box', alignItems: 'center' }}>
        <div className="search-box" style={{ flex: 1, minWidth: 0 }}>
          <Search size={14} className="search-icon" />
          <input placeholder="Search by order ID, customer, product..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <button className="btn btn-ghost btn-sm" onClick={handleRefresh} title="Refresh" style={{ width: 34, height: 34, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <RefreshCw size={13} />
          </button>
          <button
            className="btn btn-sm"
            onClick={exportAllCSV}
            disabled={exportLoading}
            style={{ background: 'var(--primary-dark)', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, padding: '0 12px', height: 34, borderRadius: 8, fontWeight: 600, opacity: exportLoading ? 0.7 : 1, whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            <Download size={13} />
            {exportLoading ? '...' : `CSV${total > 0 ? ` (${total})` : ''}`}
          </button>
          {newOrderPopupOrders.length > 0 && (
            <button
              className="btn btn-sm"
              onClick={() => setShowNewOrderPopup(true)}
              style={{ background: 'var(--green)', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, padding: '0 10px', height: 34, borderRadius: 8, fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              <Bell size={13} /> {newOrderPopupOrders.length}
            </button>
          )}
          {readyPopupOrders.length > 0 && (
            <button
              className="btn btn-sm"
              onClick={() => setShowPopup(true)}
              style={{ background: '#f97316', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, padding: '0 10px', height: 34, borderRadius: 8, fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              <Bell size={13} /> {readyPopupOrders.length}
            </button>
          )}
        </div>
      </div>

      {/* MOBILE: Infinite scroll cards */}
      {isMobile ? (
        <div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 48 }}><div className="spinner" style={{ margin: 'auto' }} /></div>
          ) : filtered.length === 0 ? (
            <div className="empty-state"><h3>No orders found</h3></div>
          ) : (
            <>
              {filtered.map(order => (
                <MobileOrderCard
                  key={order.orderId}
                  order={order}
                  onView={setSelectedOrderId}
                  onDispatch={(id) => handleStatusUpdate(id, 'OUT_FOR_DELIVERY')}
                  onDeliver={(id) => handleStatusUpdate(id, 'DELIVERED')}
                  updatingId={updatingId}
                />
              ))}

              {/* Infinite scroll loader sentinel */}
              <div ref={loaderRef} style={{ textAlign: 'center', padding: '16px 0 8px' }}>
                {mobileLoadingMore && <div className="spinner" style={{ margin: 'auto' }} />}
                {!mobileLoadingMore && !mobileHasMore && mobileOrders.length > 0 && (
                  <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    All {total} orders loaded
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      ) : (
        /* DESKTOP: Paginated table */
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Order ID</th><th>Customer</th><th>Seller</th><th>Products</th>
                  <th>Amount</th><th>Status</th><th>Date</th>
                  <th style={{ textAlign: 'center' }}>Dispatch</th>
                  <th style={{ textAlign: 'center' }}>Mark Delivered</th>
                  <th>View</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={10} style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: 'auto' }} /></td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={10}><div className="empty-state"><h3>No orders found</h3></div></td></tr>
                ) : filtered.map(order => (
                  <tr key={order.orderId} style={order.status === 'READY' ? { background: '#fff7ed' } : {}}>
                    <td>
                      <span style={{ color: 'var(--primary-dark)', fontFamily: 'var(--mono)', fontSize: 12 }}>{maskOrderId(order.displayId || order.orderId)}</span>
                      {order.status === 'READY' && <span style={{ marginLeft: 6, fontSize: 10, background: '#f97316', color: '#fff', borderRadius: 4, padding: '1px 5px', fontWeight: 700 }}>READY</span>}
                    </td>
                    <td style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{order.customerName}</td>
                    <td>{order.sellerName}</td>
                    <td style={{ maxWidth: 180 }}>
                      {order.productNames
                        ? <span title={order.productNames} style={{ display: 'block', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 170 }}>{order.productNames}</span>
                        : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>}
                    </td>
                    <td style={{ color: 'var(--green)', fontWeight: 700 }}>₹{Number(order.amount).toLocaleString('en-IN')}</td>
                    <td><span className={`badge ${order.status?.toLowerCase().replaceAll('_', '-')}`}>{order.status?.replaceAll('_', ' ') || '—'}</span></td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{order.createdAt ? new Date(order.createdAt).toLocaleDateString('en-IN') : '—'}</td>
                    <td style={{ textAlign: 'center' }}>
                      {order.status === 'READY' ? (
                        <button className="btn btn-sm" style={{ background: '#f97316', color: '#fff', fontSize: 11, padding: '4px 10px', borderRadius: 6, display: 'inline-flex', alignItems: 'center', gap: 4 }} onClick={() => handleStatusUpdate(order.orderId, 'OUT_FOR_DELIVERY')} disabled={updatingId === order.orderId}>
                          {updatingId === order.orderId ? '...' : <><Truck size={12} /> Dispatch</>}
                        </button>
                      ) : order.status === 'OUT_FOR_DELIVERY' ? (
                        <span style={{ fontSize: 11, color: '#f97316', fontWeight: 600 }}>🚚 On the way</span>
                      ) : <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>—</span>}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {order.status === 'OUT_FOR_DELIVERY' ? (
                        <button className="btn btn-sm" style={{ background: 'var(--green)', color: '#fff', fontSize: 11, padding: '4px 10px', borderRadius: 6, display: 'inline-flex', alignItems: 'center', gap: 4 }} onClick={() => handleStatusUpdate(order.orderId, 'DELIVERED')} disabled={updatingId === order.orderId}>
                          {updatingId === order.orderId ? '...' : <><CheckCircle size={12} /> Delivered</>}
                        </button>
                      ) : order.status === 'DELIVERED' ? (
                        <span style={{ fontSize: 11, color: 'var(--green)', fontWeight: 600 }}>✓ Done</span>
                      ) : <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>—</span>}
                    </td>
                    <td><button className="btn btn-ghost btn-sm btn-icon" onClick={() => setSelectedOrderId(order.orderId)} title="View"><Eye size={14} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Desktop pagination */}
          <div className="pagination" style={{ marginTop: 14 }}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{filtered.length} of {total} orders</span>
            <div className="pagination-controls">
              <button className="btn btn-ghost btn-sm" disabled={page === 0} onClick={() => setPage(p => p - 1)} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <ChevronLeft size={14} /> Prev
              </button>
              <span style={{ padding: '6px 12px', fontSize: 13 }}>{page + 1} / {Math.max(1, totalPages)}</span>
              <button className="btn btn-ghost btn-sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                Next <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedOrderId && <OrderDetailModal orderId={selectedOrderId} onClose={() => setSelectedOrderId(null)} />}
    </Layout>
  );
}